// Commands that operate on a raster layer's optional mask (`layer.mask`).
//
// Layer 4 (Commands / History). MUST remain DOM-free.
//
// Grouped in one file because they share a domain (mask lifecycle on a single
// layer) and because the helpers — finding the target raster layer, building a
// fresh full-coverage mask buffer, immutable rewriting of the mask field —
// would otherwise be duplicated across five separate command files.
//
// The five commands are:
//
//   - AddLayerMaskCommand     create a mask (reveal-all / hide-all /
//                             from-selection / from-transparency)
//   - DeleteLayerMaskCommand  remove the mask outright
//   - ApplyLayerMaskCommand   bake mask alpha into the layer's RGBA alpha
//                             and remove the mask
//   - EnableLayerMaskCommand  toggle the mask's `enabled` flag
//   - PaintMaskStrokeCommand  before/after pixel patch on the mask buffer
//                             (the mask-mode counterpart of PaintStrokeCommand)

import type { Document } from "../document/Document";
import type { Layer, LayerMask, RasterLayer } from "../document/Layer";
import type { BBox } from "../pixel/brush";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Command } from "./Command";

// ---------------------------------------------------------------------------
// Public commands
// ---------------------------------------------------------------------------

export type AddLayerMaskKind =
  | "reveal-all"
  | "hide-all"
  | "from-selection"
  | "from-transparency";

export class AddLayerMaskCommand implements Command {
  readonly kind = "add-layer-mask";
  constructor(
    private readonly layerId: string,
    private readonly maskKind: AddLayerMaskKind,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;
    const mask = buildMask(layer, doc, this.maskKind);
    if (!mask) return doc;
    return { ...doc, layers: replaceMask(doc.layers, this.layerId, mask) };
  }

  invert(before: Document, _after: Document): Command {
    const original = findRaster(before.layers, this.layerId);
    const prior = original?.mask ?? null;
    return new RestoreMaskCommand(this.layerId, prior);
  }
}

export class DeleteLayerMaskCommand implements Command {
  readonly kind = "delete-layer-mask";
  constructor(private readonly layerId: string) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer || !layer.mask) return doc;
    return { ...doc, layers: replaceMask(doc.layers, this.layerId, undefined) };
  }

  invert(before: Document, _after: Document): Command {
    const original = findRaster(before.layers, this.layerId);
    const prior = original?.mask ?? null;
    return new RestoreMaskCommand(this.layerId, prior);
  }
}

export class ApplyLayerMaskCommand implements Command {
  readonly kind = "apply-layer-mask";
  // Captured on first apply so invert can restore both the mask object and
  // the pre-bake RGBA alpha.
  private priorMask: LayerMask | null = null;
  private priorAlpha: Uint8ClampedArray | null = null;

  constructor(private readonly layerId: string) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer || !layer.mask) return doc;
    if (this.priorMask === null) {
      this.priorMask = layer.mask;
      this.priorAlpha = new Uint8ClampedArray(layer.pixels.data);
    }
    bakeMaskIntoLayerAlpha(layer);
    return { ...doc, layers: replaceMask(doc.layers, this.layerId, undefined) };
  }

  invert(_before: Document, _after: Document): Command {
    return new RestoreMaskAndAlphaCommand(
      this.layerId,
      this.priorMask,
      this.priorAlpha,
    );
  }
}

export class EnableLayerMaskCommand implements Command {
  readonly kind = "enable-layer-mask";
  constructor(
    private readonly layerId: string,
    private readonly enabled: boolean,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer || !layer.mask) return doc;
    if (layer.mask.enabled === this.enabled) return doc;
    const next: LayerMask = { ...layer.mask, enabled: this.enabled };
    return { ...doc, layers: replaceMask(doc.layers, this.layerId, next) };
  }

  invert(before: Document, _after: Document): Command {
    const original = findRaster(before.layers, this.layerId);
    const prev = original?.mask?.enabled ?? !this.enabled;
    return new EnableLayerMaskCommand(this.layerId, prev);
  }
}

export class PaintMaskStrokeCommand implements Command {
  readonly kind = "paint-mask-stroke";
  constructor(
    private readonly layerId: string,
    private readonly bbox: BBox,
    private readonly before: Uint8ClampedArray,
    private readonly after: Uint8ClampedArray,
  ) {}

  apply(doc: Document): Document {
    this.writePatch(doc, this.after);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    return new PaintMaskStrokeCommand(
      this.layerId,
      this.bbox,
      this.after,
      this.before,
    );
  }

  private writePatch(doc: Document, patch: Uint8ClampedArray): void {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer || !layer.mask) return;
    const buf = layer.mask.pixels;
    const { x, y, w, h } = this.bbox;
    const data = buf.data;
    for (let row = 0; row < h; row++) {
      const dstOff = ((y + row) * buf.width + x) * 4;
      const srcOff = row * w * 4;
      data.set(patch.subarray(srcOff, srcOff + w * 4), dstOff);
    }
    buf.markDirty();
  }
}

// ---------------------------------------------------------------------------
// Helpers exported for tests / callers
// ---------------------------------------------------------------------------

/**
 * Snapshot a rectangular region of a mask's pixel buffer. Mirrors
 * `snapshotRegion` in PaintStrokeCommand, but reads from `mask.pixels`.
 */
export function snapshotMaskRegion(
  layer: RasterLayer,
  bbox: BBox,
): Uint8ClampedArray | null {
  if (!layer.mask) return null;
  const buf = layer.mask.pixels;
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  const data = buf.data;
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * buf.width + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(data.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Internal restore commands (NOT exported)
// ---------------------------------------------------------------------------

/**
 * Inverse of AddLayerMaskCommand and DeleteLayerMaskCommand. Sets the mask
 * field back to whatever it was before (including `null`, which means "no
 * mask"). Re-inverting walks the document at that point in history so the
 * round-trip remains symmetric.
 */
class RestoreMaskCommand implements Command {
  readonly kind = "restore-layer-mask";
  constructor(
    private readonly layerId: string,
    private readonly mask: LayerMask | null,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;
    const current = layer.mask ?? null;
    if (current === this.mask) return doc;
    return {
      ...doc,
      layers: replaceMask(doc.layers, this.layerId, this.mask ?? undefined),
    };
  }

  invert(before: Document, _after: Document): Command {
    const original = findRaster(before.layers, this.layerId);
    return new RestoreMaskCommand(this.layerId, original?.mask ?? null);
  }
}

/**
 * Inverse of ApplyLayerMaskCommand. Restores both the captured mask and the
 * captured RGBA alpha (and full pixels — the snapshot is the whole buffer).
 */
class RestoreMaskAndAlphaCommand implements Command {
  readonly kind = "restore-mask-and-alpha";
  constructor(
    private readonly layerId: string,
    private readonly mask: LayerMask | null,
    private readonly pixelsSnapshot: Uint8ClampedArray | null,
  ) {}

  apply(doc: Document): Document {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return doc;
    if (this.pixelsSnapshot && layer.pixels.data.length === this.pixelsSnapshot.length) {
      layer.pixels.data.set(this.pixelsSnapshot);
      layer.pixels.markDirty();
    }
    return {
      ...doc,
      layers: replaceMask(doc.layers, this.layerId, this.mask ?? undefined),
    };
  }

  invert(before: Document, _after: Document): Command {
    const layer = findRaster(before.layers, this.layerId);
    if (!layer) {
      return new RestoreMaskAndAlphaCommand(this.layerId, null, null);
    }
    const snap = new Uint8ClampedArray(layer.pixels.data);
    return new RestoreMaskAndAlphaCommand(this.layerId, layer.mask ?? null, snap);
  }
}

// ---------------------------------------------------------------------------
// Tree walking + mask construction
// ---------------------------------------------------------------------------

function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && l.kind === "raster") return l;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function replaceMask(
  layers: readonly Layer[],
  id: string,
  mask: LayerMask | undefined,
): Layer[] {
  return layers.map((l) => {
    if (l.id === id && l.kind === "raster") {
      // Build a fresh raster-layer object. Omitting the field entirely when
      // `mask` is undefined keeps `layer.mask` strictly `undefined` rather
      // than a present-but-undefined property — important for equality checks
      // in the renderer cache.
      const next: RasterLayer = mask
        ? { ...l, mask }
        : (() => {
            const { mask: _drop, ...rest } = l;
            void _drop;
            return rest as RasterLayer;
          })();
      return next;
    }
    if (l.kind === "group") {
      return { ...l, children: replaceMask(l.children, id, mask) };
    }
    return l;
  });
}

/**
 * Allocate a fresh mask `PixelBuffer` sized to the layer's pixels. R/G/B = 0
 * throughout; alpha holds the mask coverage. `fillAlpha` is broadcast into
 * the alpha channel.
 */
function makeMaskBuffer(width: number, height: number, fillAlpha: number): PixelBuffer {
  const buf = new PixelBuffer(width, height);
  // Default fill is already 0,0,0,0 (Uint8ClampedArray). If fillAlpha != 0,
  // we set every 4th byte.
  if (fillAlpha !== 0) {
    const data = buf.data;
    for (let i = 3; i < data.length; i += 4) {
      data[i] = fillAlpha;
    }
  }
  return buf;
}

/**
 * Construct the requested mask for a given layer + document. Returns null if
 * the requested mask kind has no valid source (e.g. "from-selection" with no
 * active selection).
 */
function buildMask(
  layer: RasterLayer,
  doc: Document,
  kind: AddLayerMaskKind,
): LayerMask | null {
  const w = layer.pixels.width;
  const h = layer.pixels.height;

  switch (kind) {
    case "reveal-all": {
      return baseMask(makeMaskBuffer(w, h, 255));
    }
    case "hide-all": {
      return baseMask(makeMaskBuffer(w, h, 0));
    }
    case "from-selection": {
      const sel = doc.selection;
      if (!sel) {
        // No selection => fall back to reveal-all (matches Photoshop, which
        // grays out the menu rather than failing, but here the command was
        // dispatched anyway, so we do the closest sane thing).
        return baseMask(makeMaskBuffer(w, h, 255));
      }
      const buf = makeMaskBuffer(w, h, 0);
      const data = buf.data;
      // Sample doc-space selection at (layer.x + xx, layer.y + yy). Outside
      // the doc returns 0 from Selection.contains, which is the right value
      // for the mask (hidden).
      for (let yy = 0; yy < h; yy++) {
        for (let xx = 0; xx < w; xx++) {
          const v = sel.contains(layer.x + xx, layer.y + yy);
          data[(yy * w + xx) * 4 + 3] = v;
        }
      }
      buf.markDirty();
      return baseMask(buf);
    }
    case "from-transparency": {
      const buf = makeMaskBuffer(w, h, 0);
      const data = buf.data;
      const src = layer.pixels.data;
      // Copy src alpha (offset 3 in RGBA) into mask alpha (also offset 3).
      for (let i = 3; i < src.length; i += 4) {
        data[i] = src[i] ?? 0;
      }
      buf.markDirty();
      return baseMask(buf);
    }
  }
}

function baseMask(pixels: PixelBuffer): LayerMask {
  return {
    pixels,
    enabled: true,
    linked: true,
    x: 0,
    y: 0,
    density: 1,
    feather: 0,
  };
}

/**
 * Mutate `layer.pixels` so its alpha channel is multiplied by the mask's
 * alpha channel (scaled by density). This is the "Apply Layer Mask" bake.
 * Pixels outside the mask footprint keep their original alpha — they were
 * never affected by the mask in the first place.
 *
 * Mutates in place; caller is responsible for cleaning up the mask object.
 */
function bakeMaskIntoLayerAlpha(layer: RasterLayer): void {
  const mask = layer.mask;
  if (!mask) return;
  const dst = layer.pixels.data;
  const src = mask.pixels.data;
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const mw = mask.pixels.width;
  const mh = mask.pixels.height;
  const ox = mask.x;
  const oy = mask.y;
  const density = clamp01(mask.density);

  // Walk the intersection of mask and layer.
  const x0 = Math.max(0, ox);
  const y0 = Math.max(0, oy);
  const x1 = Math.min(lw, ox + mw);
  const y1 = Math.min(lh, oy + mh);
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const di = (y * lw + x) * 4 + 3;
      const si = ((y - oy) * mw + (x - ox)) * 4 + 3;
      const m = ((src[si] ?? 0) * density) | 0;
      const a = dst[di] ?? 0;
      dst[di] = (a * m + 127) >> 8; // (a * m) / 255 ≈ (a*m + 127) >> 8
    }
  }
  layer.pixels.markDirty();
}

function clamp01(v: number): number {
  if (!(v >= 0)) return 0;
  if (v > 1) return 1;
  return v;
}
